import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Download, Loader2, MessageSquare } from "lucide-react";
import { usePlatformApi } from "../hooks/usePlatformApi";
import type { FeedbackRecord } from "../types/feedback";
import {
  EMPTY_FILTER,
  FeedbackFilters,
  type FeedbackFilterState,
} from "../components/feedback/FeedbackFilters";
import { FeedbackDetailPane, PolarityIcon } from "../components/feedback/FeedbackDetailPane";
import { FeedbackCharts } from "../components/feedback/FeedbackCharts";

const PAGE_SIZE = 50;

/**
 * Cross-org feedback browser for platform admins (Phase C — list +
 * filters + charts; Phase D — triage controls in the detail pane).
 *
 * Layout: filter bar across the top → table on the left + detail pane on
 * the right (master/detail). Charts panel above the filters shows
 * grouped counts for the current org/date slice. Selection is URL-bound
 * (`/feedback/:feedbackId` deep-link) so triage decisions are shareable.
 */
export function FeedbackBrowser() {
  const api = usePlatformApi();
  const navigate = useNavigate();
  const { feedbackId: urlFeedbackId } = useParams<{ feedbackId?: string }>();

  const [filter, setFilter] = useState<FeedbackFilterState>(EMPTY_FILTER);
  const [items, setItems] = useState<FeedbackRecord[]>([]);
  const [cursor, setCursor] = useState<string | undefined>();
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // The URL is the source of truth for selection: /feedback/:id selects;
  // /feedback (no id) clears. Filter changes are intentionally URL-quiet —
  // if the selected record falls out of the new filter window the URL
  // stays put and the detail pane shows the "not in view" empty state.
  const selectedId = urlFeedbackId ?? null;
  const setSelectedId = useCallback(
    (id: string | null) => {
      navigate(id ? `/feedback/${id}` : "/feedback");
    },
    [navigate],
  );

  const buildParams = useCallback(
    (append: boolean) => ({
      limit: PAGE_SIZE,
      cursor: append ? cursor : undefined,
      orgId: filter.orgId || undefined,
      surface: filter.surface || undefined,
      polarity: filter.polarity || undefined,
      status: filter.status || undefined,
      category: filter.category || undefined,
      q: filter.q || undefined,
      fromDate: filter.fromDate || undefined,
      toDate: filter.toDate || undefined,
    }),
    [filter, cursor],
  );

  const load = useCallback(
    async (append: boolean) => {
      try {
        if (!append) setLoading(true);
        const res = await api.listFeedback(buildParams(append));
        setItems((prev) => (append ? [...prev, ...res.data] : res.data));
        setHasMore(res.meta.pagination.hasMore);
        setCursor(res.meta.pagination.nextCursor);
        setError(null);
      } catch (e) {
        setError(e instanceof Error ? e.message : "Failed to load feedback");
      } finally {
        setLoading(false);
      }
    },
    [api, buildParams],
  );

  // Refetch from the start whenever any filter changes. Don't drop the
  // URL selection — if the deep-linked record is in the new result set,
  // it'll re-bind via `selected = items.find(...)`. If it's filtered out,
  // the detail pane shows the "not in current view" empty state below.
  useEffect(() => {
    setCursor(undefined);
    load(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filter.orgId,
    filter.surface,
    filter.polarity,
    filter.status,
    filter.category,
    filter.q,
    filter.fromDate,
    filter.toDate,
  ]);

  const selected = useMemo(
    () => items.find((r) => r.feedbackId === selectedId) ?? null,
    [items, selectedId],
  );

  // Optimistically splice the updated row in place after a successful
  // triage save — avoids a full refetch and keeps the user's scroll
  // position + selection.
  const onTriaged = useCallback((updated: FeedbackRecord) => {
    setItems((prev) =>
      prev.map((r) => (r.feedbackId === updated.feedbackId ? updated : r)),
    );
  }, []);

  // Phase F.6 — CSV export. Bearer auth means we can't use a plain
  // anchor; fetch as Response, read into a Blob, programmatically
  // click an object-URL anchor, then revoke. Standard SPA pattern.
  const [exporting, setExporting] = useState(false);
  const onExportCsv = useCallback(async () => {
    setExporting(true);
    setError(null);
    try {
      const res = await api.exportFeedbackCsv({
        orgId: filter.orgId || undefined,
        surface: filter.surface || undefined,
        polarity: filter.polarity || undefined,
        status: filter.status || undefined,
        category: filter.category || undefined,
        q: filter.q || undefined,
        fromDate: filter.fromDate || undefined,
        toDate: filter.toDate || undefined,
      });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `feedback-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e instanceof Error ? e.message : "CSV export failed");
    } finally {
      setExporting(false);
    }
  }, [api, filter]);

  // Stats charts share the org/date slice but ignore paging + free-text.
  const filterSlice = useMemo(
    () => ({
      orgId: filter.orgId || undefined,
      fromDate: filter.fromDate || undefined,
      toDate: filter.toDate || undefined,
    }),
    [filter.orgId, filter.fromDate, filter.toDate],
  );

  return (
    <div className="flex h-full flex-col gap-4">
      <header className="flex items-center gap-2">
        <MessageSquare className="h-5 w-5 text-indigo-500" />
        <h1 className="text-xl font-semibold">User Feedback</h1>
        <span className="text-sm text-slate-400 dark:text-slate-500">
          Cross-org browse + triage
        </span>
        <button
          type="button"
          onClick={onExportCsv}
          disabled={exporting}
          className="ml-auto flex items-center gap-1.5 rounded-md border border-slate-200 px-3 py-1.5 text-sm hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:hover:bg-slate-800"
          title="Download the current filter slice as CSV (≤ 10k rows)"
        >
          {exporting ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Download className="h-4 w-4" />
          )}
          Export CSV
        </button>
      </header>

      <FeedbackCharts filterSlice={filterSlice} />

      <FeedbackFilters value={filter} onChange={setFilter} />

      {error && (
        <div className="rounded-md bg-red-50 p-3 text-sm text-red-700 dark:bg-red-950 dark:text-red-400">
          {error}
        </div>
      )}

      <div className="grid min-h-0 flex-1 grid-cols-[minmax(0,3fr)_minmax(0,2fr)] gap-4">
        <FeedbackList
          items={items}
          loading={loading}
          hasMore={hasMore}
          onLoadMore={() => load(true)}
          selectedId={selectedId}
          onSelect={setSelectedId}
        />
        <FeedbackDetailPane
          record={selected}
          selectedId={selectedId}
          onTriaged={onTriaged}
        />
      </div>
    </div>
  );
}

interface ListProps {
  items: FeedbackRecord[];
  loading: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  selectedId: string | null;
  onSelect: (id: string) => void;
}

function FeedbackList({ items, loading, hasMore, onLoadMore, selectedId, onSelect }: ListProps) {
  return (
    <div className="flex min-h-0 flex-col rounded-md border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900">
      <div className="flex items-center justify-between border-b border-slate-200 px-3 py-2 dark:border-slate-700">
        <span className="text-sm font-semibold">
          {items.length} record{items.length === 1 ? "" : "s"}
        </span>
        {loading && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto">
        {!loading && items.length === 0 && (
          <div className="p-6 text-center text-sm text-slate-400">
            No feedback matches these filters.
          </div>
        )}
        <ul className="divide-y divide-slate-100 dark:divide-slate-800">
          {items.map((r) => (
            <li key={r.feedbackId}>
              <button
                type="button"
                onClick={() => onSelect(r.feedbackId)}
                className={`w-full px-3 py-2 text-left text-sm transition-colors ${
                  selectedId === r.feedbackId
                    ? "bg-indigo-50 dark:bg-indigo-950"
                    : "hover:bg-slate-50 dark:hover:bg-slate-800"
                }`}
              >
                <div className="flex items-center gap-2">
                  <PolarityIcon polarity={r.polarity} className="h-3.5 w-3.5" />
                  <span className="rounded bg-slate-200 px-1.5 py-0.5 font-mono text-xs dark:bg-slate-700">
                    {r.surface}
                  </span>
                  <span className="ml-auto text-xs text-slate-400">
                    {new Date(r.createdAt).toLocaleDateString()}
                  </span>
                </div>
                <div className="mt-1 truncate text-xs text-slate-600 dark:text-slate-400">
                  {r.comment ?? <span className="italic text-slate-400">(no comment)</span>}
                </div>
                <div className="mt-0.5 text-xs text-slate-400">
                  org <span className="font-mono">{r.orgId}</span> · status{" "}
                  <span>{r.status}</span>
                  {r.aiEvaluation && (
                    <span className="ml-2 rounded bg-indigo-100 px-1.5 py-0.5 text-indigo-700 dark:bg-indigo-900 dark:text-indigo-300">
                      {r.aiEvaluation.category}
                    </span>
                  )}
                </div>
              </button>
            </li>
          ))}
        </ul>
      </div>
      {hasMore && (
        <div className="border-t border-slate-200 p-2 text-center dark:border-slate-700">
          <button
            type="button"
            onClick={onLoadMore}
            disabled={loading}
            className="rounded-md border border-slate-200 px-3 py-1 text-sm hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:hover:bg-slate-800"
          >
            Load more
          </button>
        </div>
      )}
    </div>
  );
}

